import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IAddWilmaUser, IUpdateWilmaUser, IUserInviteProps, IWilmaUser } from "../models/auth/WilmaUser";
import { DefaultResponseWithData } from "../models/system";

export default function useWilmaUsers() {
  const { get, post, put } = useApiHelper();
  const queryClient = useQueryClient();

  // Through our API rather than the SSO so it follows SAV impersonation, like the invite below
  const wilmaUsers = useQuery({
    queryKey: ["wilmaUsers"],
    queryFn: () => get<DefaultResponseWithData<IWilmaUser[]>>("/customer/users"),
    refetchInterval: 1000 * 10,
  });

  const inviteWilmaUser = useMutation(
    (data: IUserInviteProps) => post(`/customer/users/invite`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["wilmaUsers"]);
      },
    }
  );

  const addWilmaUser = useMutation(
    (data: IAddWilmaUser) => post(`/customer/users`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["wilmaUsers"]);
      },
    }
  );

  const updatePassword = useMutation(
    (data: { userId: string; password: string }) =>
      put(`/customer/users/${data.userId}/password`, { password: data.password })
  );

  const updateWilmaUser = useMutation(
    ({ userId, ...body }: IUpdateWilmaUser) => put(`/customer/users/${userId}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["wilmaUsers"]);
      },
    }
  );

  return {
    wilmaUsers,
    inviteWilmaUser,
    addWilmaUser,
    updatePassword,
    updateWilmaUser,
  };
}
